import { useEffect, useState, type FormEvent } from "react";
import { startRegistration } from "@simplewebauthn/browser";
import { api, message, type Me, type Passkey } from "../api";

export function Account({ me }: { me: Me }) {
  const [keys, setKeys] = useState<Passkey[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  async function load() {
    try { setKeys(await api.get<Passkey[]>("/api/account/passkeys")); }
    catch (err) { setError(message(err)); setKeys([]); }
  }
  useEffect(() => { void load(); }, []);

  async function enrol() {
    setBusy(true); setError(null); setOk(null);
    try {
      const options = await api.post("/auth/passkey/register/options");
      const attestation = await startRegistration({ optionsJSON: options as never });
      const label = deviceLabel();
      await api.post("/auth/passkey/register/verify", { label, response: attestation });
      setOk(`Passkey "${label}" enrolled. It's required from your next sign-in.`);
      await load();
    } catch (err) { setError(message(err)); }
    finally { setBusy(false); }
  }

  async function remove(id: string) {
    setError(null); setOk(null);
    try { await api.del(`/api/account/passkeys/${id}`); await load(); }
    catch (err) { setError(message(err)); }
  }

  return (
    <>
      <div className="pagehead"><h1>Account</h1></div>

      <section className="card">
        <h2>{me.name}</h2>
        <p className="muted mono small">{me.email} · {me.role}</p>
      </section>

      <ChangePassword />

      <section className="card">
        <h2>Passkeys</h2>
        <p className="muted small">
          A passkey is your second factor. Your password alone won't sign you in
          once one is enrolled.
        </p>

        {error && <div className="alert" role="alert">{error}</div>}
        {ok && <div className="notice notice--inline">{ok}</div>}

        {keys === null ? <p className="muted">Loading…</p>
          : keys.length === 0 ? (
            <p className="warn">
              No passkey enrolled — this account is protected by password only.
            </p>
          ) : (
            <table className="table">
              <thead><tr><th>Device</th><th>Added</th><th>Last used</th><th></th></tr></thead>
              <tbody>
                {keys.map((k) => (
                  <tr key={k.id}>
                    <td>{k.label}</td>
                    <td className="muted small">{new Date(k.createdAt).toLocaleDateString()}</td>
                    <td className="muted small">
                      {k.lastUsedAt ? new Date(k.lastUsedAt).toLocaleString() : "never"}
                    </td>
                    <td>
                      <button className="linkbtn" onClick={() => void remove(k.id)}>Remove</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

        <button className="btn btn--inline" onClick={enrol} disabled={busy}>
          {busy ? "Waiting for device…" : "Add a passkey"}
        </button>

        <p className="hint">
          Passkeys are tied to this exact hostname. One enrolled here on
          dev-portal will not work on the production portal, and the other way
          round.
        </p>
      </section>
    </>
  );
}

function ChangePassword() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null); setOk(false);
    if (next !== confirm) { setError("The two new passwords don't match."); return; }
    setBusy(true);
    try {
      await api.post("/auth/password", { current, next });
      setCurrent(""); setNext(""); setConfirm(""); setOk(true);
    } catch (err) { setError(message(err)); }
    finally { setBusy(false); }
  }

  return (
    <section className="card">
      <h2>Password</h2>
      <form className="card--form" onSubmit={submit}>
        {error && <div className="alert" role="alert">{error}</div>}
        {ok && <div className="notice notice--inline">Password changed. Your other sessions were signed out.</div>}
        <div className="field">
          <label htmlFor="curpw">Current password</label>
          <input id="curpw" type="password" autoComplete="current-password" required
                 value={current} onChange={(e) => setCurrent(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="nextpw">New password</label>
          <input id="nextpw" type="password" autoComplete="new-password" required minLength={12}
                 value={next} onChange={(e) => setNext(e.target.value)} />
          <p className="hint">At least 12 characters.</p>
        </div>
        <div className="field">
          <label htmlFor="confpw">Confirm new password</label>
          <input id="confpw" type="password" autoComplete="new-password" required
                 value={confirm} onChange={(e) => setConfirm(e.target.value)} />
        </div>
        <button className="btn btn--inline" type="submit" disabled={busy}>
          {busy ? "Saving…" : "Change password"}
        </button>
      </form>
    </section>
  );
}

/** A human label so a list of passkeys is distinguishable later. */
function deviceLabel(): string {
  const ua = navigator.userAgent;
  const os = /Windows/.test(ua) ? "Windows"
    : /Mac OS X|Macintosh/.test(ua) ? "Mac"
    : /Android/.test(ua) ? "Android"
    : /iPhone|iPad/.test(ua) ? "iOS" : "Device";
  const browser = /Edg\//.test(ua) ? "Edge"
    : /Chrome\//.test(ua) ? "Chrome"
    : /Firefox\//.test(ua) ? "Firefox"
    : /Safari\//.test(ua) ? "Safari" : "Browser";
  return `${browser} on ${os}`;
}
